const AD_TELEMETRY_VERSION = 2
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0']

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined'

const makeId = (prefix) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`

const safeStringify = (data) => {
	try {
		return JSON.stringify(data)
	} catch (error) {
		return JSON.stringify({ error: 'stringify_failed' })
	}
}

const getAndroidBridge = () => {
	if (!isBrowser()) return null
	return window.JSCallAndroid || null
}

export const callAndroidBridge = (method, ...args) => {
	const bridge = getAndroidBridge()
	if (!bridge || typeof bridge[method] !== 'function') return false

	try {
		bridge[method](...args)
		return true
	} catch (error) {
		console.warn(`[AdTelemetryV2] ${method} failed`, error)
		return false
	}
}

export const createStatsPvId = () => {
	if (isBrowser() && window.crypto && typeof window.crypto.randomUUID === 'function') {
		return window.crypto.randomUUID()
	}
	return makeId('spv')
}

export const createStatsExtra = (statsPvId, extraParams = {}) => safeStringify({
	v: 1,
	stats_pv_id: statsPvId,
	...extraParams,
})

export const callStatsEventWithRetry = (eventName, url = '', extra = '', options = {}) => {
	if (!isBrowser()) return false

	const retryMs = options.retryMs || 250
	const timeoutMs = options.timeoutMs || 8000
	const startedAt = Date.now()
	let reported = false
	let timer = null

	const clearTimer = () => {
		if (timer) {
			clearTimeout(timer)
			timer = null
		}
	}

	const report = () => {
		if (reported) return true
		if (callAndroidBridge('statsEvent', eventName, url, extra)) {
			reported = true
			clearTimer()
			return true
		}
		if (Date.now() - startedAt >= timeoutMs) return false
		clearTimer()
		timer = setTimeout(report, retryMs)
		return false
	}

	return report()
}

const detectPageType = (path) => {
	if (path === '/' || path === '/index.html') return 'home'
	if (path.includes('/wiki/') || path.includes('/detail/')) return 'detail'
	if (path.includes('/tag/') || path.includes('/tags/')) return 'tag'
	if (path.includes('/novel/')) return 'novel'
	return 'other'
}

const getPageContext = () => {
	const { location } = window
	return {
		host: location.hostname,
		path: location.pathname,
		query: location.search,
		url: location.href,
		title: document.title,
		referrer: document.referrer || null,
		pageType: detectPageType(location.pathname),
	}
}

const getViewportContext = () => ({
	windowWidth: window.innerWidth,
	windowHeight: window.innerHeight,
	scrollX: window.scrollX || window.pageXOffset || 0,
	scrollY: window.scrollY || window.pageYOffset || 0,
	documentWidth: document.documentElement.scrollWidth,
	documentHeight: document.documentElement.scrollHeight,
	devicePixelRatio: window.devicePixelRatio || 1,
	isMobile: window.innerWidth <= 768,
})

const getIframeSrcHost = (iframe) => {
	const src = iframe && iframe.getAttribute('src')
	if (!src) return null
	try {
		return new URL(src, window.location.href).hostname || null
	} catch (error) {
		return null
	}
}

const classifyAd = (ins) => {
	const className = ins.className || ''
	const slotId = ins.getAttribute('data-ad-slot')
	const clientId = ins.getAttribute('data-ad-client') || ''
	const inlineStyle = ins.getAttribute('style') || ''
	const isAutoAd = className.includes('adsbygoogle-noablate') || !slotId
	let adType = isAutoAd ? 'auto_ads' : 'manual_inline'

	if (className.includes('adsbygoogle-noablate')) {
		adType = 'auto_anchor_bottom'
	} else if (inlineStyle.includes('display:inline-block') || inlineStyle.includes('display:block')) {
		adType = 'manual_inline_lazy'
	}

	return {
		network: isAutoAd ? 'auto_ads' : 'adsense',
		adType,
		slotId,
		clientId,
		configKeys: [ins.getAttribute('data-ad-slot-key'), ins.getAttribute('data-config-key')].filter(Boolean),
		className,
	}
}

export const createAdTelemetryV2 = (options = {}) => {
	const settings = {
		scrollDebounce: 300,
		resizeDebounce: 500,
		enableScroll: true,
		enableResize: true,
		enablePagehide: true,
		sendLegacyAdPosition: true,
		enableProductionSend: true,
		allowLocalValidation: true,
		...options,
	}

	let pageViewId = ''
	let initTimer = null
	let scrollTimer = null
	let resizeTimer = null
	let mutationObserver = null
	let statusObserver = null
	let initialized = false
	let clickedAdInstanceId = null
	let clickedSlotId = null
	const adStateMap = new Map()
	const observedStatusElements = new WeakSet()
	const observedClickIframes = new WeakSet()
	const clickTimers = new Map()

	const collectAdInstance = (ins, index) => {
		const rect = ins.getBoundingClientRect()
		const iframe = ins.querySelector('iframe')
		const iframeRect = iframe && iframe.getBoundingClientRect()
		const adInstanceId = `ad_${index + 1}`
		const existing = adStateMap.get(adInstanceId) || {}
		const classification = classifyAd(ins)
		const hasIframe = Boolean(iframe)
		const adStatus = ins.getAttribute('data-ad-status') || (hasIframe ? 'loaded' : 'pending')
		const computedStyle = window.getComputedStyle(ins)
		const visibleLeft = Math.max(rect.left, 0)
		const visibleTop = Math.max(rect.top, 0)
		const visibleRight = Math.min(rect.right, window.innerWidth)
		const visibleBottom = Math.min(rect.bottom, window.innerHeight)
		const visibleWidth = Math.max(0, visibleRight - visibleLeft)
		const visibleHeight = Math.max(0, visibleBottom - visibleTop)
		const totalArea = rect.width * rect.height
		const visibilityRatio = totalArea > 0 ? Math.round((visibleWidth * visibleHeight / totalArea) * 100) : 0
		const isInViewport = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth
		const now = Date.now()

		const instance = {
			adInstanceId,
			domIndex: index,
			tagName: ins.tagName.toLowerCase(),
			network: classification.network,
			adType: classification.adType,
			classificationSource: 'dom_inferred',
			sourceComponent: ins.closest('[data-component]')?.getAttribute('data-component') || null,
			renderMode: hasIframe ? 'lazy_observer' : null,
			configKeys: classification.configKeys,
			slotId: classification.slotId,
			clientId: classification.clientId,
			adFormat: ins.getAttribute('data-ad-format'),
			fullWidthResponsive: ins.getAttribute('data-full-width-responsive'),
			adStatus,
			adsbygoogleStatus: ins.getAttribute('data-adsbygoogle-status'),
			anchorStatus: classification.adType === 'auto_anchor_bottom' ? 'displayed' : null,
			anchorShown: classification.adType === 'auto_anchor_bottom',
			hasIframe,
			iframeCount: hasIframe ? 1 : 0,
			iframeId: iframe?.id || null,
			iframeName: iframe?.name || null,
			iframeSrcHost: getIframeSrcHost(iframe),
			viewportX: Math.round(rect.left),
			viewportY: Math.round(rect.top),
			pageX: Math.round(rect.left + (window.scrollX || window.pageXOffset || 0)),
			pageY: Math.round(rect.top + (window.scrollY || window.pageYOffset || 0)),
			insWidth: Math.round(rect.width),
			insHeight: Math.round(rect.height),
			containerX: Math.round(rect.left),
			containerY: Math.round(rect.top),
			containerWidth: Math.round(rect.width),
			containerHeight: Math.round(rect.height),
			iframeWidth: iframeRect ? Math.round(iframeRect.width) : 0,
			iframeHeight: iframeRect ? Math.round(iframeRect.height) : 0,
			isInViewport,
			visibilityRatio,
			visibleWidth: Math.round(visibleWidth),
			visibleHeight: Math.round(visibleHeight),
			positionMode: computedStyle.position === 'fixed' ? 'fixed' : 'document_flow',
			anchorEdge: classification.adType === 'auto_anchor_bottom' ? 'bottom' : null,
			cssTop: computedStyle.top,
			cssBottom: computedStyle.bottom,
			zIndex: computedStyle.zIndex,
			className: classification.className,
			firstSeenAt: existing.firstSeenAt || now,
			firstInViewportAt: isInViewport ? existing.firstInViewportAt || now : existing.firstInViewportAt || null,
			firstFilledAt: adStatus === 'filled' || adStatus === 'loaded' ? existing.firstFilledAt || now : existing.firstFilledAt || null,
		}

		adStateMap.set(adInstanceId, {
			firstSeenAt: instance.firstSeenAt,
			firstInViewportAt: instance.firstInViewportAt,
			firstFilledAt: instance.firstFilledAt,
		})

		return instance
	}

	const collectAllAds = () => Array.from(document.querySelectorAll('ins.adsbygoogle')).map(collectAdInstance)

	const buildSummary = (ads) => ({
		totalAds: ads.length,
		manualAds: ads.filter(ad => ad.adType.startsWith('manual')).length,
		autoAds: ads.filter(ad => ad.adType.startsWith('auto')).length,
		adxAds: ads.filter(ad => ad.network === 'adx').length,
		visibleAds: ads.filter(ad => ad.isInViewport).length,
		filledAds: ads.filter(ad => ad.adStatus === 'filled' || ad.adStatus === 'loaded').length,
		unfilledAds: ads.filter(ad => ad.adStatus === 'unfilled').length,
		anchorAds: ads.filter(ad => ad.anchorShown).length,
		clickedAdInstanceId,
		clickedSlotId,
	})

	const buildVisibleAds = () => collectAllAds()
		.filter(ad => ad.isInViewport && ad.insWidth > 0 && ad.insHeight > 0)
		.map(ad => ({
			insX: ad.viewportX,
			insY: ad.viewportY,
			insHeight: ad.insHeight,
			windowWidth: window.innerWidth,
			windowHeight: window.innerHeight,
		}))

	const buildPayload = (eventType, triggerSource, extra = {}) => {
		const ads = collectAllAds()
		return {
			version: AD_TELEMETRY_VERSION,
			pageViewId,
			eventType,
			triggerSource,
			timestamp: Date.now(),
			timestampIso: new Date().toISOString(),
			page: getPageContext(),
			viewport: getViewportContext(),
			summary: buildSummary(ads),
			extra,
			ads,
		}
	}

	const sendLegacyAdPosition = () => {
		if (!settings.sendLegacyAdPosition) return
		callAndroidBridge('adPosition', safeStringify(buildVisibleAds()))
	}

	const send = (payload) => {
		if (!isBrowser()) return false
		sendLegacyAdPosition()
		const message = safeStringify(payload)
		if (settings.enableProductionSend && callAndroidBridge('adTelemetryV2', message)) {
			return true
		}

		if (settings.allowLocalValidation && LOCAL_HOSTS.includes(window.location.hostname)) {
			console.log('[AdTelemetryV2]', payload.eventType, payload.summary)
		}
		return false
	}

	const report = (eventType = 'init', triggerSource = 'manual', extra = {}) => {
		if (!isBrowser() || !initialized) return false
		if (eventType === 'click') {
			clickedSlotId = extra.adSlot || extra['data-ad-slot'] || extra.slotId || clickedSlotId
		}
		return send(buildPayload(eventType, triggerSource, extra))
	}

	const handleScroll = () => {
		if (scrollTimer) clearTimeout(scrollTimer)
		scrollTimer = setTimeout(() => {
			report('scroll', 'window_scroll')
		}, settings.scrollDebounce)
	}

	const handleResize = () => {
		if (resizeTimer) clearTimeout(resizeTimer)
		resizeTimer = setTimeout(() => {
			report('resize', 'window_resize')
		}, settings.resizeDebounce)
	}

	const handlePagehide = () => report('pagehide', 'pagehide')
	const handleStatusChange = (mutations = []) => {
		const mutation = mutations.find(item => item.target && item.target.getAttribute)
		const target = mutation && mutation.target
		report('status_change', 'ad_status_change', {
			adSlot: target?.getAttribute('data-ad-slot') || null,
			adStatus: target?.getAttribute('data-ad-status') || null,
		})
	}

	const handleClick = (ins, triggerSource = 'iframe_focus') => {
		const ads = Array.from(document.querySelectorAll('ins.adsbygoogle'))
		const index = ads.indexOf(ins)
		clickedAdInstanceId = index >= 0 ? `ad_${index + 1}` : 'ad_unknown'
		clickedSlotId = ins.getAttribute('data-ad-slot')
		report('click', triggerSource, {
			adSlot: clickedSlotId,
		})
	}

	const setupClickDetection = (iframe, ins) => {
		if (observedClickIframes.has(iframe)) return
		observedClickIframes.add(iframe)
		const timer = setInterval(() => {
			if (document.activeElement === iframe) {
				clearInterval(timer)
				clickTimers.delete(iframe)
				handleClick(ins)
			}
		}, 200)
		clickTimers.set(iframe, timer)
	}

	const setupKnownAdIframes = () => {
		document.querySelectorAll('ins.adsbygoogle iframe').forEach((iframe) => {
			const ins = iframe.closest('ins.adsbygoogle')
			if (ins) setupClickDetection(iframe, ins)
		})
	}

	const setupStatusObservers = () => {
		if (!statusObserver) return
		document.querySelectorAll('ins.adsbygoogle').forEach((ins) => {
			if (observedStatusElements.has(ins)) return
			observedStatusElements.add(ins)
			statusObserver.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })
		})
	}

	const observeDom = () => {
		setupKnownAdIframes()
		setupStatusObservers()
	}

	const init = () => {
		if (!isBrowser() || initialized || !document.body) return false
		initialized = true
		pageViewId = makeId('adpv')
		statusObserver = new MutationObserver(handleStatusChange)
		mutationObserver = new MutationObserver(observeDom)
		mutationObserver.observe(document.body, { childList: true, subtree: true })
		observeDom()
		if (settings.enableScroll) window.addEventListener('scroll', handleScroll, { passive: true })
		if (settings.enableResize) window.addEventListener('resize', handleResize, { passive: true })
		if (settings.enablePagehide) window.addEventListener('pagehide', handlePagehide)
		initTimer = setTimeout(() => report('init', 'adTelemetryV2_init'), 1000)
		return true
	}

	const cleanup = () => {
		if (initTimer) clearTimeout(initTimer)
		if (scrollTimer) clearTimeout(scrollTimer)
		if (resizeTimer) clearTimeout(resizeTimer)
		clickTimers.forEach(timer => clearInterval(timer))
		clickTimers.clear()
		mutationObserver && mutationObserver.disconnect()
		statusObserver && statusObserver.disconnect()
		if (isBrowser()) {
			window.removeEventListener('scroll', handleScroll)
			window.removeEventListener('resize', handleResize)
			window.removeEventListener('pagehide', handlePagehide)
		}
		initialized = false
		mutationObserver = null
		statusObserver = null
		adStateMap.clear()
	}

	return {
		buildPayload,
		cleanup,
		handleClick,
		init,
		report,
		send,
	}
}
